import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Download, 
  FileText, 
  Calendar,
  ArrowLeft,
  CheckCircle,
  Clock,
  Database,
  User,
  BookOpen,
  Award
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/contexts/AuthContext'

interface ExportOption {
  id: string
  name: string
  description: string
  icon: any
  format: 'PDF' | 'CSV' | 'JSON' | 'Excel'
  size: string
  lastGenerated?: string
  includes: string[]
}

const exportOptions: ExportOption[] = [
  {
    id: 'profile',
    name: 'Profile Data',
    description: 'Export your personal information and preferences',
    icon: User,
    format: 'JSON',
    size: '2.3 KB',
    includes: ['Personal info', 'Settings', 'Preferences']
  },
  {
    id: 'courses',
    name: 'Course Progress',
    description: 'Export your course enrollment and progress data',
    icon: BookOpen,
    format: 'CSV',
    size: '15.7 KB',
    includes: ['Course list', 'Progress', 'Grades', 'Certificates']
  },
  {
    id: 'assignments',
    name: 'Assignments',
    description: 'Export all your submitted assignments and grades',
    icon: FileText,
    format: 'PDF',
    size: '8.2 MB',
    includes: ['Submissions', 'Grades', 'Feedback', 'Due dates']
  },
  {
    id: 'certificates',
    name: 'Certificates',
    description: 'Export your earned certificates and achievements',
    icon: Award,
    format: 'PDF',
    size: '12.4 MB',
    includes: ['Certificates', 'Badges', 'Achievements', 'Completion dates']
  },
  {
    id: 'activity',
    name: 'Activity Log',
    description: 'Export your learning activity and engagement data',
    icon: Calendar,
    format: 'CSV',
    size: '5.1 KB',
    includes: ['Login history', 'Course activity', 'Time spent', 'Interactions']
  },
  {
    id: 'complete',
    name: 'Complete Data Export',
    description: 'Export all your data in a comprehensive package',
    icon: Database,
    format: 'JSON',
    size: '45.8 MB',
    includes: ['All data types', 'Complete history', 'Full backup']
  }
]

export function ExportPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [exportingItems, setExportingItems] = useState<Set<string>>(new Set())
  const [completedExports, setCompletedExports] = useState<Set<string>>(new Set())

  const handleExport = async (option: ExportOption) => {
    setExportingItems(prev => new Set(prev).add(option.id))
    
    try {
      // Simulate export process
      await new Promise(resolve => setTimeout(resolve, 3000))
      
      // Simulate file download
      const blob = new Blob(['Mock export data'], { type: 'application/octet-stream' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${option.name.toLowerCase().replace(/\s+/g, '-')}.${option.format.toLowerCase()}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
      setCompletedExports(prev => new Set(prev).add(option.id))
    } catch (error) {
      console.error('Export error:', error)
      alert('Failed to export data')
    } finally {
      setExportingItems(prev => {
        const newSet = new Set(prev)
        newSet.delete(option.id)
        return newSet
      })
    }
  }

  const isExporting = (optionId: string) => exportingItems.has(optionId)
  const isCompleted = (optionId: string) => completedExports.has(optionId)

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3 sm:space-x-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/more')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Export Data</h1>
          <p className="text-sm sm:text-base text-gray-600">Download your data and reports</p>
        </div>
      </div>

      {/* Export Options */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {exportOptions.map((option) => (
          <Card key={option.id} className="p-4 sm:p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start space-x-3 sm:space-x-4 mb-3 sm:mb-4">
              <div className="p-2 sm:p-3 bg-primary-100 rounded-lg">
                <option.icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">{option.name}</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-2">{option.description}</p>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline" className="text-xs">
                    {option.format}
                  </Badge>
                  <span className="text-xs text-gray-500">{option.size}</span>
                </div>
              </div>
            </div>

            <div className="mb-3 sm:mb-4">
              <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">Includes:</h4>
              <ul className="text-xs sm:text-sm text-gray-600 space-y-1">
                {option.includes.map((item, index) => (
                  <li key={index} className="flex items-center space-x-2">
                    <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                    <span className="truncate">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Button
              onClick={() => handleExport(option)}
              disabled={isExporting(option.id)}
              className={`w-full ${
                isCompleted(option.id)
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-primary-600 hover:bg-primary-700'
              }`}
            >
              {isExporting(option.id) ? (
                <>
                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : isCompleted(option.id) ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Downloaded
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export {option.format}
                </>
              )}
            </Button>
          </Card>
        ))}
      </div>

      {/* Export History */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Exports</h3>
        <div className="space-y-3">
          {completedExports.size > 0 ? (
            Array.from(completedExports).map((exportId) => {
              const option = exportOptions.find(opt => opt.id === exportId)
              return option ? (
                <div key={exportId} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <h4 className="font-medium text-gray-900">{option.name}</h4>
                      <p className="text-sm text-gray-600">
                        Exported as {option.format} • {new Date().toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    Download Again
                  </Button>
                </div>
              ) : null
            })
          ) : (
            <p className="text-gray-500 text-center py-8">No recent exports</p>
          )}
        </div>
      </Card>

      {/* Data Privacy Notice */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Data Privacy</h3>
        <p className="text-sm text-blue-800 mb-3">
          Your exported data is processed securely and only contains information you have access to. 
          Exports are generated on-demand and are not stored on our servers.
        </p>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Exports are generated in real-time and may take a few moments</li>
          <li>• Large exports may be split into multiple files</li>
          <li>• Downloaded files are automatically deleted after 24 hours</li>
          <li>• You can request a complete data deletion at any time</li>
        </ul>
      </Card>
    </div>
  )
}